import React, { useState, useEffect, useRef } from "react";
import { useUser } from "../useUser";
import { useNavigate, useLocation } from "react-router-dom";
import AdminLineItem from "@/components/AdminLineItem";
import { BrandButton, RolesSelector, Toast } from "@/components";
import useToast from "@/components/useToast";
import PowerBIEmbedComponent from "@/components/PowerBIEmbedComponent";
import SearchableSelect from "@/components/SearchableSelect";
import { useAuthenticatedApi } from "@/utils/callApi";
import typo from "@/styles/Typography.module.css";
import styles from "@/styles/Gradients.module.css";
import { Table, Badge, Form, Button, Modal, Alert, Tabs, Tab, InputGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import { NavLink } from "react-router-dom";

// Brand color constants for consistency
const BRAND = {
  PRIMARY: "#F16D09",    // Primary buttons, accents
  ALERT: "#DC3D17",      // Alerts, emphasis
  BACKGROUND: "#FCED21", // Backgrounds, highlights
  CARD: "#FFC60B",       // Cards, accents
  DARK: "#2E1719",       // Headings, dark cards
  BODY: "#492C28",       // Body, subcards
};

export default function ReportMonitor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { secureHelpers } = useAuthenticatedApi();
  const { toast, showToast, hideToast } = useToast();
  
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]); // Need this to match current user by email
  
  // Filter state
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [permissionFilter, setPermissionFilter] = useState('');
  
  // Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const [previewEmbedUrl, setPreviewEmbedUrl] = useState(null);
  const [previewEmbedToken, setPreviewEmbedToken] = useState(null);
  const [previewEmbedReportId, setPreviewEmbedReportId] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedPreviewUserId, setSelectedPreviewUserId] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  
  // Track previous pathname to detect navigation from ReportCreator
  const prevPathnameRef = useRef(null);
  
  // Load data on mount
  useEffect(() => {
    loadReports();
    loadRoles();
    loadUsers();
  }, []);
  
  // Reload data only when coming from ReportCreator
  useEffect(() => {
    const prevPath = prevPathnameRef.current;
    const currentPath = location.pathname;
    
    // Reload if we just navigated from ReportCreator to here
    if (prevPath && prevPath.includes('/reports/reportCreator') && currentPath.includes('/reports/reportMonitor')) {
      loadReports();
      loadRoles();
    }
    
    // Update the ref for next time
    prevPathnameRef.current = currentPath;
  }, [location.pathname]);
  
  // Load reports from API
  const loadReports = async () => {
    try {
      setLoading(true);
      const data = await secureHelpers.get('/api/Reports');
      
      // The list endpoint doesn't include roles, so fetch full details for each report
      const reportsWithRoles = await Promise.all(
        data.map(async (report) => {
          try {
            const fullReport = await secureHelpers.get(`/api/Reports/${report.id}`);
            console.log(`✳️ Loaded full details for report ${report.id}:`, fullReport);

            return fullReport;
          } catch (err) {
            console.error(`Failed to load full details for report ${report.id}:`, err);
            return report; // Return basic report if full details fail
          }
        })
      );
      
      // Transform backend data to match component structure
      const transformedReports = reportsWithRoles.map(report => ({
        id: report.id || report.Id,
        title: report.title || report.Title || '',
        description: report.description || report.Description || '',
        adminNotes: report.adminNotes || report.AdminNotes || '',
        url: report.uri || report.Uri || '',
        creationDate: report.creationDate || report.CreationDate,
        viewCount: report.viewCount || report.ViewCount || 0,
        isLive: report.isLive || report.IsLive || false,
        roles: report.roles || report.Roles || [],
        // Use creation date as available from for now
        availableFrom: report.creationDate || report.CreationDate,
        availableTo: null,
        lastAccessed: null,
      }));
      
      setReports(transformedReports);
    } catch (err) {
      console.error("Failed to load reports:", err);
      showToast({
        title: "Error",
        message: "Failed to load reports. Please try again.",
        variant: "error"
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Load roles from API
  const loadRoles = async () => {
    try {
      const data = await secureHelpers.get('/api/roles');
      // Transform roles to permission-like structure for now
      const transformedRoles = data.map(role => ({
        code: role.id.toString(),
        label: role.name,
        description: role.description || ''
      }))
      setRoles(transformedRoles);
      
    } catch (err) {
      console.error("Failed to load roles:", err);
      showToast({
        title: "Error",
        message: "Failed to load roles.",
        variant: "error"
      });
    }
  };

  // Load users for preview selection
  const loadUsers = async () => {
    try {
      const usersData = await secureHelpers.get(`/api/Members`);
      setUsers(usersData);
    } catch (err) {
      console.error("Failed to load users:", err);
      showToast({
        title: "Error",
        message: "Failed to load users.",
        variant: "error"
      });
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  
  // Handle report selection
  const handleReportSelect = (reportId) => {
    const selected = reports.find(r => r.id === parseInt(reportId));
    setSelectedReport(selected || null);
  };
  
  // Get status badge
  const getStatusBadge = (isLive) => {
    return isLive ? 
      <Badge style={{backgroundColor: BRAND.PRIMARY, fontSize: "var(--font-sm)", padding: "6px 12px"}}>Live</Badge> :
      <Badge style={{backgroundColor: BRAND.BODY, fontSize: "var(--font-sm)", padding: "6px 12px"}}>Not Live</Badge>;
  };
  
  // Navigate to report creator for new report
  const handleAddReport = () => {
    navigate('/admin/reports/reportCreator', { state: { fromMonitor: true } });
  };
  
  // Navigate to report creator for editing
  const handleEditReport = (report) => {
    navigate('/admin/reports/reportCreator', { 
      state: { 
        fromMonitor: true, 
        reportId: report.id 
      } 
    });
  };
  
  // View report with embed preview - opens modal for user selection
  // Clear preview when user selection changes
  useEffect(() => {
    // Clear the embedded report when user changes (but keep modal open)
    if (selectedPreviewUserId !== null) {
      setPreviewEmbedUrl(null);
      setPreviewEmbedToken(null);
      setPreviewEmbedReportId(null);
    }
  }, [selectedPreviewUserId]);
  
  const handleViewReport = (report) => {
    setPreviewReport(report);
    setSelectedPreviewUserId(null); // Reset user selection
    setPreviewEmbedUrl(null);
    setPreviewEmbedToken(null);
    setPreviewEmbedReportId(null);
    setShowPreviewModal(true);
  };
  
  // Load preview after user selection
  const loadReportPreview = async () => {
    if (!selectedPreviewUserId) {
      showToast({
        title: "Select User",
        message: "Please select a user to preview as",
        variant: "warning"
      });
      return;
    }
    
    if (!previewReport) return;
    
    setPreviewError(null);
    try {
      setPreviewLoading(true);
      
      // Fetch report details
      console.log(`Fetching report: /api/Reports/${previewReport.id}`);
      const reportData = await secureHelpers.get(`/api/Reports/${previewReport.id}`);
      console.log('Report data received:', reportData);
      
      // Fetch embed token with selected user ID
      console.log(`Fetching token with userId: ${selectedPreviewUserId}`);
      const tokenData = await secureHelpers.get(`/api/Reports/${previewReport.id}/GetEmbedDetails/${selectedPreviewUserId}`);
      console.log('Token data received:', tokenData);
        // check if response is a 503 with HTML body (Power BI service might be down)
      if (reportData?.status === 503 && reportData?.headers['content-type']?.includes('text/html')) {
        showToast({
        title: "Service Unavailable",
        message: "Power BI service is currently unavailable. Please try again later.",
        variant: "error"
      });
      } else {
        console.log("✳️Checked and no 503 response");
      }
      
      setPreviewEmbedUrl(tokenData.embedUrl || tokenData.EmbedUrl);
      setPreviewEmbedToken(tokenData.embedToken || tokenData.EmbedToken);
      setPreviewEmbedReportId(tokenData.embedReportId || tokenData.EmbedReportId);
      
      showToast({
        title: "Success",
        message: "Report preview loaded successfully!",
        variant: "success"
      });
    } catch (err) {
      console.error("Failed to load report preview:", err);
      
      // Clear any existing preview
      setPreviewEmbedUrl(null);
      setPreviewEmbedToken(null);
      setPreviewEmbedReportId(null);
      
      // Determine error message based on error type
      let errorMessage = "Failed to load preview. ";
      
      // Check for 503 Service Unavailable (Power BI service down)
      if (err.message?.includes("503") || err.status === 503 || err.response?.status === 503) {
        errorMessage = "Power BI service is currently unavailable. Please try again later.";
      // Check for 403 Forbidden (permission denied)
      } else if (err.message?.includes("403") || err.status === 403 || err.response?.status === 403) {
        errorMessage = "This user does not have permission to view this report. Please select a user with the required roles or assign the necessary permissions.";
      } else if (err.message?.includes("404") || err.status === 404 || err.response?.status === 404) {
        errorMessage = "Report not found. It may have been deleted or is no longer available.";
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += "An unexpected error occurred. Please try again.";
      }
      
      setPreviewError(errorMessage);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Toggle report live status
  const toggleReportStatus = async (reportId) => {
    try {
      const report = reports.find(r => r.id === reportId);
      if (!report) return;
      
      const newIsLiveValue = !report.isLive;
      
      // Fetch full report details to get roles (list endpoint doesn't include them)
      const fullReport = await secureHelpers.get(`/api/Reports/${reportId}`);
      
      const payload = {
        id: fullReport.id || fullReport.Id,
        title: fullReport.title || fullReport.Title,
        description: fullReport.description || fullReport.Description,
        adminNotes: fullReport.adminNotes || fullReport.AdminNotes,
        roles: fullReport.roles || fullReport.Roles || [], // Backend returns array of role IDs
        isLive: newIsLiveValue
      };
      
      // Optimistically update UI immediately
      setReports(prevReports => 
        prevReports.map(r => 
          r.id === reportId ? { ...r, isLive: newIsLiveValue } : r
        )
      );
      
      await secureHelpers.put(`/api/Reports/${reportId}`, payload);
      
      showToast({
        title: "Success",
        message: `Report ${newIsLiveValue ? 'activated' : 'deactivated'} successfully!`,
        variant: "success"
      });
      
      // TODO: Uncomment this line when backend supports isLive in response
      await loadReports();
    } catch (err) {
      console.error("Failed to toggle report status:", err);
      
      // Revert optimistic update on error
      setReports(prevReports => 
        prevReports.map(r => 
          r.id === reportId ? { ...r, isLive: !newIsLiveValue } : r
        )
      );
      
      showToast({
        title: "Error",
        message: "Failed to update report status. Please try again.",
        variant: "error"
      });
    }
  };
  
  // Filter reports based on active filter and search term
  const filteredReports = reports.filter(report => {
    // Filter by active status
    if (activeFilter === 'active' && !report.isLive) return false;
    if (activeFilter === 'inactive' && report.isLive) return false;
    
    // Filter by search term
    if (searchTerm && !report.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Filter by permission
    if (permissionFilter && (!report.roles || !report.roles.includes(permissionFilter))) return false;
    
    return true;
  });
  
  return (
    <AdminLineItem>
      <div className="mb-4">
        <h1 style={{color: BRAND.PRIMARY, paddingBottom: "1.5rem", marginTop: "2rem", fontSize: "var(--font-3xl)"}}>Report Monitoring Dashboard</h1>
        <p style={{color: BRAND.BODY, fontSize: "var(--font-subtitle)"}}>
          Manage all reports and control access roles. Add new reports or update existing ones.
        </p>
      </div>
      
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{color: BRAND.PRIMARY}} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3" style={{color: BRAND.BODY}}>Loading reports...</p>
        </div>
      ) : (
        <>
      
      {/* Reports Summary */}
      <div className="card mb-4">
        <div className="card-header" style={{backgroundColor: BRAND.PRIMARY, borderRadius:"0px"}}>
          <h4 style={{color: "white", margin: "0.5rem 0", fontSize: "var(--font-heading-sm)"}}>Reports Summary</h4>
        </div>
        <div className="card-body">
          <div className="row">
            {/* All Reports */}
            <div className="col-md-12 mb-3">
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="card-title" style={{color: BRAND.DARK, fontSize: "var(--font-subtitle)"}}>All Reports - overview</h5>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-3">
                      <div className="mb-2">
                        <span style={{color: BRAND.BODY, fontSize: "var(--font-lg)", marginRight: "0.5rem"}}>Total Reports:</span>
                        <span style={{fontSize: "var(--font-lg)", color: BRAND.DARK}}>
                          {reports.length}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="mb-2">
                        <span style={{color: BRAND.BODY, fontSize: "var(--font-lg)", marginRight: "0.5rem"}}>Live:</span>
                        <span style={{color: BRAND.BODY, fontSize: "var(--font-lg)"}}>
                          {reports.filter(r => r.isLive).length}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="mb-2">
                        <span style={{color: BRAND.BODY, fontSize: "var(--font-lg)", marginRight: "0.5rem"}}>Not Live:</span>
                        <span style={{color: BRAND.BODY, fontSize: "var(--font-lg)"}}>
                          {reports.filter(r => !r.isLive).length}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="mb-2">
                        <span style={{color: BRAND.BODY, fontSize: "var(--font-lg)", marginRight: "0.5rem"}}>Roles Used:</span>
                        <span style={{fontSize: "var(--font-lg)", color: BRAND.DARK}}>
                          {new Set(reports.flatMap(r => r.roles || [])).size}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Reports Filtering and Search */}
      <div className="card mb-4">
        <div className="card-header" style={{backgroundColor: BRAND.PRIMARY, borderRadius:"0px"}}>
          <div className="d-flex justify-content-between align-items-center">
            <h4 style={{color: "white", margin: "0.5rem 0", fontSize: "var(--font-heading-sm)"}}>Reports Management</h4>
            <Button 
              style={{
                backgroundColor: BRAND.ALERT,
                color: "white",
                fontSize: "var(--font-heading-sm)",
                padding: "6px 12px"
              }}
              onClick={handleAddReport}
            >
              Add New Report
            </Button>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-4">
            <div className="col-md-4">
              <Form.Group>
                <Form.Label style={{color: BRAND.DARK, fontSize: "var(--font-heading-sm)"}}>Filter by Status</Form.Label>
                <Form.Select 
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                  style={{fontSize: "var(--font-heading-sm)", height: "42px"}}
                >
                  <option value="all">All Reports</option>
                  <option value="active">Active Reports</option>
                  <option value="inactive">Inactive Reports</option>
                </Form.Select>
              </Form.Group>
            </div>
            
            <div className="col-md-4">
              <Form.Group>
                <Form.Label style={{color: BRAND.DARK, fontSize: "var(--font-heading-sm)"}}>Filter by Role</Form.Label>
                <Form.Select 
                  value={permissionFilter}
                  onChange={(e) => setPermissionFilter(e.target.value)}
                  style={{fontSize: "var(--font-heading-sm)", height: "42px"}}
                >
                  <option value="">All Roles</option>
                  {roles.map(perm => (
                    <option key={perm.code} value={perm.code}>
                      {perm.label} ({perm.code})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            
            <div className="col-md-4">
              <Form.Group>
                <Form.Label style={{color: BRAND.DARK, fontSize: "var(--font-heading-sm)"}}>Search Reports</Form.Label>
                <Form.Control 
                  type="text"
                  placeholder="Search by report title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{fontSize: "var(--font-heading-sm)", height: "42px"}}
                />
              </Form.Group>
            </div>
          </div>
          
          {/* Reports Table */}
          <Table hover responsive className="mt-2" style={{fontSize: "var(--font-heading-sm)"}}>
            <thead>
              <tr style={{color: BRAND.DARK}}>
                <th style={{fontSize: "var(--font-heading-sm)"}}>Title</th>
                <th style={{fontSize: "var(--font-heading-sm)"}}>Status</th>
                <th style={{fontSize: "var(--font-heading-sm)"}}>Created</th>
                <th style={{fontSize: "var(--font-heading-sm)"}}>Roles</th>
                <th style={{fontSize: "var(--font-heading-sm)"}}>Views</th>
                <th style={{fontSize: "var(--font-heading-sm)"}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.map(report => (
                <tr 
                  key={report.id}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedReport?.id === report.id ? `${BRAND.PRIMARY}40` : null
                  }}
                  onClick={() => handleReportSelect(report.id)}
                >
                  <td style={{color: BRAND.DARK, fontSize: "var(--font-heading-sm)", fontWeight: "500"}}>
                    {report.title}
                  </td>
                  <td>{getStatusBadge(report.isLive)}</td>
                  <td style={{fontSize: "var(--font-heading-sm)"}}>{formatDate(report.creationDate)}</td>
                  <td>
                    {report.roles && report.roles.length > 0 ? (
                      <ul style={{listStyleType: "none", margin: 0, paddingLeft: "1.2rem", fontSize: "var(--font-heading-sm)"}}>
                        {report.roles.map(roleId => {
                          const role = report.roles.find(r => r.code === roleId.toString());
                          return (
                            <li key={roleId} style={{color: BRAND.BODY, fontSize: "var(--font-lg)"}}>
                              {role?.label || roleId}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <span style={{color: BRAND.BODY, fontSize: "var(--font-sm)"}}>No roles assigned</span>
                    )}
                  </td>
                  <td style={{fontSize: "var(--font-heading-sm)", textAlign: "center"}}>{report.viewCount}</td>
                  <td style={{width: "1%", whiteSpace: "nowrap"}}>
                    <div className="btn-group btn-group-sm">
                      <Button 
                        style={{
                          backgroundColor: BRAND.PRIMARY,
                          color: "white",
                          borderColor: BRAND.PRIMARY,
                          fontSize: "var(--font-heading-sm)",
                          padding: "6px 12px"
                        }} 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewReport(report);
                        }}
                        title="Preview report"
                      >
                        View
                      </Button>
                      <Button 
                        style={{
                          backgroundColor: BRAND.ALERT,
                          color: "white",
                          borderColor: BRAND.ALERT,
                          fontSize: "var(--font-heading-sm)",
                          padding: "6px 12px"
                        }} 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditReport(report);
                        }}
                      >
                        Edit
                      </Button>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            This button will change the availability of the report to members with the correct role/s allocated
                          </Tooltip>
                        }
                      >
                        <Button
                          variant="primary"
                          style={{
                            backgroundColor: report.isLive ? BRAND.BODY : BRAND.PRIMARY,
                            color: "white",
                            borderColor: report.isLive ? BRAND.BODY : BRAND.PRIMARY,
                            fontSize: "var(--font-heading-sm)",
                            padding: "6px 12px"
                          }} 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReportStatus(report.id);
                          }}
                        >
                          {report.isLive ? "Disable" : "Enable"}
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {filteredReports.length === 0 && (
            <div className="text-center p-4" style={{color: BRAND.BODY, fontSize: "var(--font-heading-sm)"}}>
              No reports match your current filters. Try adjusting your search criteria.
            </div>
          )}
        </div>
        <div className="card-footer">
          <span style={{color: BRAND.BODY, fontSize: "var(--font-heading-sm)"}}>
            Showing {filteredReports.length} of {reports.length} reports
          </span>
        </div>
      </div>
      
      {/* Selected Report Details */}
      {selectedReport && (
        <div className="card mb-4">
          <div className="card-header" style={{backgroundColor: BRAND.PRIMARY, borderRadius:"0px"}}>
            <h4 style={{color: "white", margin: "0.5rem 0", fontSize: "var(--font-heading-sm)"}}>
              Report Details: {selectedReport.title}
            </h4>
          </div>
          <div className="card-body">
            <div className="row">
              <div 
              // className="col-md-8"
              >
                <h5 style={{color: BRAND.DARK, fontSize: "var(--font-base)", marginBottom: "20px"}}>Report Information</h5>
                
                <div className="mb-3">
                  <div style={{color: BRAND.BODY, fontSize: "var(--font-heading-sm)"}}>
                    <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Description:</span>
                  </div>
                  <div style={{fontSize: "var(--font-heading-sm)", marginBottom: "10px"}}>{selectedReport.description}</div>
                </div>
                
                <div className="mb-3">
                  <div style={{color: BRAND.BODY, fontSize: "var(--font-heading-sm)"}}>
                    <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Admin Notes:</span>
                  </div>
                  <div style={{fontSize: "var(--font-heading-sm)", marginBottom: "10px"}}>{selectedReport.adminNotes}</div>
                </div>
                
                <div className="mb-3">
                  <div style={{color: BRAND.BODY, fontSize: "var(--font-heading-sm)"}}>
                    <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>URL:</span>
                  </div>
                  <a 
                    href={selectedReport.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{fontSize: "var(--font-heading-sm)", color: BRAND.PRIMARY, wordBreak: "break-all"}}
                  >
                    {selectedReport.url}
                  </a>
                </div>
                
                <div className="row mb-3">
                  <div>
                    <div style={{color: BRAND.BODY, fontSize: "var(--font-heading-sm)"}}>
                      <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Available From:</span>
                    </div>
                    <div style={{fontSize: "var(--font-heading-sm)"}}>{formatDate(selectedReport.availableFrom)}</div>
                  </div>
                  <div className="col-md-6">
                  </div>
                </div>
                
                <div className="mb-3">
                  <div style={{color: BRAND.BODY, fontSize: "var(--font-heading-sm)"}}>
                    <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Role Groups:</span>
                  </div>
                  <div style={{fontSize: "var(--font-heading-sm)"}}>
                    {selectedReport.roles && selectedReport.roles.map(pCode => {
                      const role = selectedReport.roles.find(r => r.code === pCode);
                      return (
                        <Badge 
                          key={pCode} 
                          style={{
                            backgroundColor: "#6c757d",
                            margin: "2px",
                            fontSize: "var(--font-xs)",
                            padding: "6px 10px"
                          }}
                        >
                          {role?.label || pCode}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              {/* <div className="col-md-4">
                <div className="card" style={{backgroundColor: "#f9f9f9"}}>
                  <div className="card-body">
                    <h5 style={{color: BRAND.DARK, fontSize: "var(--font-base)", marginBottom: "20px"}}>Usage Statistics</h5>
                    
                    <div className="mb-3">
                      <div style={{color: BRAND.BODY, fontSize: "var(--font-heading-sm)"}}>
                        <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Status:</span>
                      </div>
                      <div style={{fontSize: "var(--font-heading-sm)"}}>{selectedReport.isActive ? "Active" : "Inactive"}</div>
                    </div>
                    
                    <div className="mb-3">
                      <div style={{color: BRAND.BODY, fontSize: "var(--font-heading-sm)"}}>
                        <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Total Views:</span>
                      </div>
                      <div style={{fontSize: "var(--font-heading-sm)"}}>
                        <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>{selectedReport.accessCount}</span>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div style={{color: BRAND.BODY, fontSize: "var(--font-heading-sm)"}}>
                        <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Last Accessed:</span>
                      </div>
                      <div style={{fontSize: "var(--font-heading-sm)"}}>{selectedReport.lastAccessed ? formatDate(selectedReport.lastAccessed) : "Never"}</div>
                    </div>
                    
                    <div className="mt-4">
                      <Button 
                        style={{
                          backgroundColor: BRAND.PRIMARY,
                          color: "white",
                          fontSize: "var(--font-heading-sm)",
                          width: "100%",
                          marginBottom: "10px"
                        }}
                        onClick={() => window.open(selectedReport.url, '_blank')}
                      >
                        Open Report
                      </Button>
                      
                      <Button 
                        style={{
                          backgroundColor: BRAND.ALERT,
                          color: "white",
                          fontSize: "var(--font-heading-sm)",
                          width: "100%",
                          marginBottom: "10px"
                        }}
                        onClick={() => handleEditReport(selectedReport)}
                      >
                        Edit Report
                      </Button>
                      
                      <Button 
                        style={{
                          backgroundColor: selectedReport.isActive ? BRAND.BODY : "#28a745",
                          color: "white",
                          fontSize: "var(--font-heading-sm)",
                          width: "100%"
                        }}
                        onClick={() => toggleReportStatus(selectedReport.id)}
                      >
                        {selectedReport.isActive ? "Disable Access" : "Enable Access"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      )}
      </>
      )}
      
      {/* Report Preview Modal */}
      <Modal 
        show={showPreviewModal} 
        onHide={() => setShowPreviewModal(false)} 
        size="xl"
        centered
      >
        <Modal.Header closeButton style={{ backgroundColor: BRAND.PRIMARY, color: "white" }}>
          <Modal.Title>
            <div>
              {previewReport ? previewReport.title : "Report Preview"}
              {previewReport?.description && (
                <div style={{ fontSize: "var(--font-xs)", fontWeight: "normal", marginTop: "0.5rem", opacity: 0.9 }}>
                  {previewReport.description}
                </div>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: previewEmbedUrl ? "15px" : "20px", minHeight: "200px" }}>
          {!previewEmbedUrl ? (
            <div>
              {previewError && (
                <Alert variant="danger" className="mb-3" onClose={() => setPreviewError(null)} dismissible>
                  <Alert.Heading>Unable to Load Report</Alert.Heading>
                  {previewError}
                </Alert>
              )}
              <Form.Group className="mb-3">
                <Form.Label>
                  <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Select User to Preview As</span>
                </Form.Label>
                <SearchableSelect
                  options={users}
                  value={selectedPreviewUserId}
                  getLabel={(u) => `${u.firstName} ${u.lastName} (${u.email})`}
                  getValue={(u) => u.id}
                  onChange={(userId) => setSelectedPreviewUserId(userId)}
                  placeholder="Search for a user..."
                />
              </Form.Group>
              <BrandButton 
                onClick={loadReportPreview} 
                disabled={!selectedPreviewUserId || previewLoading}
              >
                {previewLoading ? "Loading..." : "Load Preview"}
              </BrandButton>
            </div>
          ) : (
            <div style={{ width: "100%" }}>
              {previewLoading ? (
                <div className="text-center p-5">
                  <div className="spinner-border" role="status" style={{ color: BRAND.PRIMARY }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading report preview...</p>
                </div>
              ) : (
                <div style={{ height: "75vh" }}>
                  <PowerBIEmbedComponent
                    embedUrl={previewEmbedUrl}
                    accessToken={previewEmbedToken}
                    reportId={previewEmbedReportId}
                    pageName="Implicit"
                  />
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ marginTop: '20px' }}>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
        {/* Toast inside modal so it appears on top */}
        {showPreviewModal && <Toast {...toast} onClose={hideToast} />}
      </Modal>
      
      {/* Toast for non-modal toasts */}
      {!showPreviewModal && <Toast {...toast} onClose={hideToast} />}
     
    </AdminLineItem>
  );
}
